import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { space, width, fontWeight, fontSize, color, borders } from 'styled-system';
import { MenuItem as BlueprintMenuItem, Classes } from '@blueprintjs/core';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { formAriaProps, guessTextFromLabel, guessByPossibleElement } from '../util/ariaUtils';
import Icon from './Icon';

const StyledMenuItem = styled(BlueprintMenuItem)`
  ${space};
  ${width};
  ${fontSize};
  ${fontWeight};
  ${color};
  ${borders};

  /* tone down the padding a bit to compress the items */
  &.${Classes.MENU_ITEM} {
    padding: 4px 7px;
  }

  &.${Classes.ACTIVE} {
    .${Classes.TAG}.${Classes.MINIMAL} {
      /* this is intended in both dark/light mode */
      background-color: #ffffff;
      color: ${(props) => props.theme.colors.blackText};
    }
  }

  &.${Classes.SMALL} {
    .${Classes.ICON} > svg {
      width: 12px;
      height: 12px;
    }
  }

  .pre-wrap {
    white-space: pre-wrap;
  }
`;

const MenuItem = (props) => {
  const {
    ariaLabel,
    staticContext,
    alignItems,
    lineHeight,
    textTransform,
    borderRadius,
    icon,
    iconColor,
    minWidth,
    small,
    className,
    monospace,
    active,
    style = {},
    ...rest
  } = props;

  const menuItemRef = useRef(null);

  useEffect(() => {
    // we debounce this bc we want to let the menu component flip the tabindex from wrapper to bp4-popover-target first (or back) and only do this if we see BOTH with tabindex=0
    const doCheckAndUpdate = debounce(() => {
      const menuItems = menuItemRef?.current?.querySelectorAll('[role="menuitem"][tabindex="0"]');

      if (menuItems?.length) {
        // IF they BOTH are tabindex=0 then we put it on the wrapper as tabindex=0 and the popover target as tabindex=-1
        // If only ONE is tabindex=0 then we let the menu component update it!
        let isSubMenuItem = false;
        for (let i = 0; i < menuItems.length; i++) {
          isSubMenuItem = menuItems[i].closest('.bp4-popover-content');
          if (!isSubMenuItem && menuItemRef.current.tabIndex === 0) {
            menuItems[i]?.setAttribute('tabindex', '-1');
          }
          // if there is tabIndex 0 inside a menu item but the wrapper is NOT tabIndex=0 then than is ok
        }
      }
    }, 500);

    const observer = new MutationObserver(() => {
      doCheckAndUpdate();
    });

    if (menuItemRef.current) {
      observer.observe(menuItemRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['tabindex', 'role'] // Only observe relevant attribute changes
      });
    }

    doCheckAndUpdate(); // Initial check

    return () => observer.disconnect();
  }, []);

  const ariaLabelGuess = rest.label ? guessTextFromLabel(rest.label) : undefined;
  const ariaTextGuess = rest.text ? guessByPossibleElement(rest.text) : undefined;

  const ariaProps = formAriaProps(
    props,
    { ariaLabel: ariaLabel || ariaTextGuess || ariaLabelGuess },
    {
      passOnExistingAria: true
    }
  );
  if (rest.role) {
    ariaProps.role = rest.role;
  } else {
    ariaProps.role = 'menuitem';
  }

  if (rest.children) {
    // clearly we only need this wrapper business of there is a submenu we need to control
    return (
      <div
        ref={menuItemRef}
        tabIndex={0}
        className="menu-item-wrapper"
        aria-label="sub menu. Tab to focus on sub menu items"
      >
        <StyledMenuItem
          {...ariaProps}
          {...rest}
          className={classNames(className, {
            [Classes.SMALL]: small,
            [Classes.ACTIVE]: active,
            [Classes.MONOSPACE_TEXT]: monospace
          })}
          style={{ alignItems, borderRadius, lineHeight, textTransform, minWidth, ...style }}
          icon={icon ? <Icon icon={icon} color={iconColor} iconSize={16} /> : undefined}
        />
      </div>
    );
  }
  return (
    <StyledMenuItem
      {...ariaProps}
      {...rest}
      tabIndex={0}
      className={classNames(className, {
        [Classes.SMALL]: small,
        [Classes.ACTIVE]: active,
        [Classes.MONOSPACE_TEXT]: monospace
      })}
      style={{ alignItems, borderRadius, lineHeight, textTransform, minWidth, ...style }}
      icon={icon ? <Icon icon={icon} color={iconColor} iconSize={16} /> : undefined}
    />
  );
};

MenuItem.defaultProps = {
  popoverProps: { openOnTargetFocus: true }
};

export default MenuItem;
